import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import cookie from "react-cookies";
import { listCreatorScripts, startCreatorScript } from "../../../services/creatorApi";
import { handleCreatorGateError } from "./creatorLicense";
import { TrainingLoading } from "../../academy/AcademyShared";
import { CreatorLayout, CREATOR_CRUMBS, CreatorStatusChip } from "./CreatorShared";
import { getScriptDeliverableLabel, isV2Brief } from "./creatorModels";
import { getCandidateId } from "../../../utils/candidateAuth";

const LANES = [
  {
    key: "ready",
    title: "Ready to start",
    match: (s) => !s.my_submission_status && !s.script_blocked && !s.is_expired,
  },
  {
    key: "progress",
    title: "Continue filming",
    match: (s) => s.my_submission_status === "in_progress" && !s.is_expired,
  },
  {
    key: "closed",
    title: "Closed. Uploads ended",
    match: (s) => s.my_submission_status === "in_progress" && Boolean(s.is_expired),
  },
  {
    key: "retake",
    title: "Needs retake",
    match: (s) => s.my_submission_status === "retake",
  },
  {
    key: "review",
    title: "In review",
    match: (s) => s.my_submission_status === "submitted",
  },
  {
    key: "done",
    title: "Approved",
    match: (s) => s.my_submission_status === "approved",
  },
  {
    key: "blocked",
    title: "Not available right now",
    match: (s) => Boolean(s.script_blocked) && !s.my_submission_status && !s.is_expired,
  },
  {
    key: "expired",
    title: "No longer available",
    match: (s) => Boolean(s.is_expired) && !s.my_submission_status,
  },
];

const HOW_VIEWS_KEY_PREFIX = "qt_creator_how_views_";
const HOW_AUTO_OPEN_VIEWS = 3;

function howViewsStorageKey() {
  const id = getCandidateId() || "anon";
  return `${HOW_VIEWS_KEY_PREFIX}${id}`;
}

function readHowViewCount() {
  try {
    const raw = window.localStorage.getItem(howViewsStorageKey());
    const n = Number.parseInt(raw || "0", 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function bumpHowViewCount() {
  const next = Math.min(readHowViewCount() + 1, HOW_AUTO_OPEN_VIEWS + 1);
  try {
    window.localStorage.setItem(howViewsStorageKey(), String(next));
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

function CreatorHowBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const views = bumpHowViewCount();
    if (views > HOW_AUTO_OPEN_VIEWS) return undefined;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setOpen(true));
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div className={`creator-how-banner${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="creator-how-banner__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="creator-how-banner__label">How it works</span>
        <span className="creator-how-banner__summary">
          Brief · Film on phone · Upload &amp; submit · Paid by EFT
        </span>
        <span className="creator-how-banner__chevron" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      <div className="creator-how-banner__panel" aria-hidden={!open}>
        <div className="creator-how-banner__panel-inner">
          <ol className="creator-how-banner__list">
            <li>Open a script brief and tap Start.</li>
            <li>Film vertical clips on your phone. A friend holds the camera.</li>
            <li>Upload every required clip, then submit for review.</li>
            <li>
              When QikTruck approves, R300 is payable. QikTruck pays by EFT after
              banking details are confirmed.
            </li>
          </ol>
          <Link className="creator-how-banner__link" to="/creator">
            Full Creator journey
          </Link>
        </div>
      </div>
    </div>
  );
}

function cardModel(script) {
  if (script.is_expired && !script.my_submission_status) {
    return {
      status: "expired",
      modifier: "done",
      blurb:
        script.unavailable_message ||
        "This script is no longer available. New uploads are closed. You can still read the brief.",
      primaryLabel: "No longer available",
      primaryTo: `/me/creator/scripts/${script.id}`,
      showBrief: true,
      startNew: false,
      locked: true,
    };
  }
  if (script.script_blocked && !script.my_submission_status) {
    const rejectNote = String(script.block_note || "").trim();
    return {
      status: "rejected",
      modifier: "done",
      blurb: rejectNote
        ? `Not approved: ${rejectNote}`
        : "This one did not pass review, so it is closed for you for now. Other scripts are still open. If we unlock this script, you can start again.",
      primaryLabel: "Unavailable",
      primaryTo: `/me/creator/scripts/${script.id}`,
      showBrief: true,
      startNew: false,
      locked: true,
    };
  }
  const status = script.my_submission_status || "available";
  const itemName = isV2Brief(script) ? "performances" : "clips";
  switch (status) {
    case "in_progress":
      if (script.is_expired) {
        return {
          status,
          modifier: "done",
          blurb:
            script.unavailable_message ||
            "This script is no longer available. New uploads are closed.",
          primaryLabel: "View status",
          primaryTo: `/me/creator/work/${script.my_submission_id}`,
          showBrief: true,
          startNew: false,
        };
      }
      return {
        status,
        modifier: "progress",
        blurb: `You started this script. Finish uploading your raw ${itemName}.`,
        primaryLabel: "Continue",
        primaryTo: `/me/creator/work/${script.my_submission_id}`,
        showBrief: true,
        startNew: false,
      };
    case "retake":
      return {
        status,
        modifier: "retake",
        blurb: `QikTruck asked for new ${itemName} on some items. Fix those only.`,
        primaryLabel: isV2Brief(script) ? "Fix performances" : "Fix clips",
        primaryTo: `/me/creator/work/${script.my_submission_id}`,
        showBrief: true,
        startNew: false,
      };
    case "submitted":
      return {
        status,
        modifier: "review",
        blurb: "Submitted. Waiting for QikTruck approval. Payment stays pending until then.",
        primaryLabel: "View status",
        primaryTo: `/me/creator/work/${script.my_submission_id}`,
        showBrief: true,
        startNew: false,
      };
    case "approved":
      return {
        status,
        modifier: "done",
        blurb: "Approved. This script is payable on the Payments tab.",
        primaryLabel: "View payments",
        primaryTo: "/me/creator/payments",
        showBrief: true,
        startNew: false,
      };
    default:
      return {
        status: "available",
        modifier: "ready",
        blurb: isV2Brief(script)
          ? "Not started. Read the brief, then record 3 hook performances and the shared body once."
          : "Not started. Read the brief, then record each shot as a separate raw clip.",
        primaryLabel: "Start script",
        primaryTo: null,
        showBrief: true,
        startNew: true,
      };
  }
}

function IntroGateBanner({ intro }) {
  if (!intro || intro.filming_unlocked) return null;
  const status = intro.status || "none";
  let title = "One quick step before paid scripts";
  let copy =
    "You can browse every script and brief. Once your short personal intro is approved, you can start and upload.";
  let cta = "Record intro";
  if (status === "submitted") {
    title = "Thanks. Your intro is in review";
    copy =
      "Browse scripts while you wait. We will unlock filming as soon as your intro is approved.";
    cta = "View intro status";
  } else if (status === "retake") {
    title = "Please send a new intro";
    copy =
      intro.retake_note ||
      "We need a clearer personal intro before paid scripts unlock. Upload a fresh clip and we will review it again.";
    cta = "Upload new intro";
  } else if (status === "rejected") {
    title = "We need a new intro from you";
    copy =
      intro.admin_note ||
      "This intro did not pass review. That is okay. Upload a fresh clip and we will take another look.";
    cta = "Upload new intro";
  } else if (status === "draft") {
    title = "Finish your personal intro";
    copy =
      "Your intro clip is uploaded. Submit it for review and we will unlock paid scripts once it is approved.";
    cta = "Finish intro";
  }

  return (
    <div className="creator-contact-banner creator-intro-banner" role="status">
      <div>
        <p className="creator-contact-banner__title">{title}</p>
        <p className="creator-contact-banner__copy">{copy}</p>
      </div>
      <Link className="training-btn-primary" to="/me/creator/intro">
        {cta}
      </Link>
    </div>
  );
}

function ScriptCard({ script, startingId, onStart, filmingUnlocked }) {
  const model = cardModel(script);
  const busy = startingId === script.id;
  const v2 = isV2Brief(script);
  const startLocked = model.startNew && !filmingUnlocked;
  const previews = v2
    ? (script.hook_variants || []).flatMap((hook) =>
        (hook.preview_image_urls || []).slice(0, 1).map((url) => ({
          key: hook.deliverable_key,
          label: hook.deliverable_key?.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          url,
        }))
      )
    : (script.shot_previews || []).map((preview) => ({
        key: `shot-${preview.shot_number}`,
        label: `Shot ${preview.shot_number}`,
        url: preview.url,
      }));

  return (
    <article
      className={`training-card creator-script-card creator-script-card--${model.modifier}`}
    >
      <div className="creator-script-card__top">
        <div className="creator-script-card__identity">
          <span className="creator-script-card__code">{script.code}</span>
          <CreatorStatusChip status={model.status} />
        </div>
        <span className="creator-script-card__pay">{script.payment_label}</span>
      </div>

      <h3 className="creator-script-card__title">{script.title}</h3>
      <p className="creator-script-card__meta">
        {script.category} · {getScriptDeliverableLabel(script)}
      </p>
      <p className="creator-script-card__blurb">{model.blurb}</p>

      {previews.length > 0 ? (
        <div className="creator-script-card__previews" aria-label="Performance previews">
          {previews.map((preview) => (
            <Link
              key={`${script.id}-${preview.key}`}
              className="creator-script-card__preview"
              to={`/me/creator/scripts/${script.id}`}
            >
              <img
                src={preview.url}
                alt={`${script.code} ${preview.label} preview`}
                loading="lazy"
              />
              <span className="creator-script-card__preview-cap">
                {preview.label}
              </span>
            </Link>
          ))}
        </div>
      ) : null}

      <div
        className={`creator-script-card__actions${
          model.showBrief ? "" : " creator-script-card__actions--single"
        }`}
      >
        {model.showBrief ? (
          <Link
            className="training-btn-secondary"
            to={`/me/creator/scripts/${script.id}`}
          >
            View brief
          </Link>
        ) : null}

        {model.locked ? (
          <span className="training-btn-secondary creator-script-card__status-btn">
            Unavailable
          </span>
        ) : model.startNew ? (
          startLocked ? (
            <Link className="training-btn-secondary" to="/me/creator/intro">
              Record intro first
            </Link>
          ) : (
            <button
              type="button"
              className="training-btn-primary"
              disabled={busy}
              onClick={() => onStart(script)}
            >
              {busy ? "Starting..." : model.primaryLabel}
            </button>
          )
        ) : (
          <Link
            className={
              model.modifier === "review" || model.modifier === "done"
                ? "training-btn-secondary creator-script-card__status-btn"
                : "training-btn-primary"
            }
            to={model.primaryTo}
          >
            {model.primaryLabel}
          </Link>
        )}
      </div>
    </article>
  );
}

export default function CreatorScripts() {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState([]);
  const [intro, setIntro] = useState(null);
  const [filmingUnlocked, setFilmingUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startingId, setStartingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = "/login?next=/me/creator";
      return;
    }
    setLoading(true);
    listCreatorScripts()
      .then((res) => {
        setScripts(res.scripts || []);
        setIntro(res.intro || null);
        setFilmingUnlocked(res.filming_unlocked !== false);
      })
      .catch((e) => setError(e.message || "Could not load scripts"))
      .finally(() => setLoading(false));
  }, []);

  const lanes = useMemo(
    () =>
      LANES.map((lane) => ({
        ...lane,
        items: scripts.filter(lane.match),
      })).filter((lane) => lane.items.length > 0),
    [scripts]
  );

  const onStart = async (script) => {
    setStartingId(script.id);
    setError("");
    try {
      if (script.my_submission_id) {
        navigate(`/me/creator/work/${script.my_submission_id}`);
        return;
      }
      const res = await startCreatorScript(script.id);
      navigate(`/me/creator/work/${res.submission.id}`);
    } catch (e) {
      if (handleCreatorGateError(e, "/me/creator")) return;
      if (e?.code === "script_expired") {
        setError(e.message || "This script is no longer available.");
        return;
      }
      if (e?.code === "script_blocked") {
        setError(
          e.message ||
            "This script is closed for you for now. Other scripts are still open."
        );
        return;
      }
      setError(e.message || "Could not start script");
    } finally {
      setStartingId(null);
    }
  };

  if (loading) {
    return (
      <CreatorLayout breadcrumbs={CREATOR_CRUMBS.scripts} backTo="/creator">
        <TrainingLoading label="Loading scripts..." />
      </CreatorLayout>
    );
  }

  const introPending = Boolean(intro) && !filmingUnlocked;

  return (
    <CreatorLayout breadcrumbs={CREATOR_CRUMBS.scripts} backTo="/creator">
      <div
        className={
          introPending ? "creator-scripts-body creator-scripts-body--intro-pending" : undefined
        }
      >
        <section className="creator-hero">
          <p className="training-kicker">Creator</p>
          <h2 className="creator-hero__title">Scripts</h2>
          <p className="creator-hero__lede">
            R300 per completed script. Start new ones below, or open work already in progress or in review.
          </p>
        </section>

        <CreatorHowBanner />
        <IntroGateBanner intro={intro} />

        {error ? <p className="creator-error">{error}</p> : null}

        {!scripts.length ? (
          <div className="training-empty">
            <p>No scripts available yet. Check again soon.</p>
          </div>
        ) : (
          lanes.map((lane) => (
            <section key={lane.key} className="creator-script-lane">
              <h3 className="creator-section-title">{lane.title}</h3>
              <div className="creator-script-list">
                {lane.items.map((script) => (
                  <ScriptCard
                    key={script.id}
                    script={script}
                    startingId={startingId}
                    onStart={onStart}
                    filmingUnlocked={filmingUnlocked}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </CreatorLayout>
  );
}
